import './featured-contents-highlight-featured.scss'

import { graphql, useStaticQuery } from 'gatsby'
import React from 'react'

import Button from '@/components/button/button'
import Image from '@/components/image/image'
import ImageLabel from '@/components/image-label'
import Link from '@/components/link/link'
import TitleLink from '@/components/title-link/title-link'
import useDefaultImages from '@/hooks/useDefaultImages'
import { generateUrl } from '@/internal/utils/generate-url'
import { getModelMessages } from '@/utils/get-model-messages'
import { isUpcomingEvent } from '@/utils/upcoming-event'

type Props = {
  title: Queries.SectionFeaturedContentsFragment['title']
  featured: Queries.SectionFeaturedContentsFragment['featured']
  filter: Queries.SectionFeaturedContentsFragment['filter']
  nodes: readonly (Highlights | null)[]
}
type Background = {
  datoCmsAsset?: {
    readonly gatsbyImageData: Record<string, unknown>
  }
}
const FeaturedContentsHighlightFeatured: React.FC<Props> = (props) => {
  const { title, filter, featured, nodes } = props
  const background: Background = useStaticQuery(graphql`
    {
      datoCmsAsset(originalId: { eq: "22410930" }) {
        gatsbyImageData(
          width: 630
          height: 328
          imgixParams: { w: "630", h: "328", fit: "crop", crop: "focalpoint" }
        )
      }
    }
  `)
  const featureContent = featured ?? nodes[0]!
  const {
    slug,
    featured: featuredImage,
    externalName,
    excerpt,
    originalId,
    __typename: typeName
  } = featureContent

  const { imgFeaturedMultipleContentFeatured } =
    useDefaultImages(typeName) ?? {}
  const modelMessages = getModelMessages(typeName)
  const modelTopicMessages = getModelMessages(filter?.__typename)
  const url = generateUrl(typeName, slug)
  return (
    <div className="highlighteds-v2">
      <div className="text-with-image">
        <Image image={background.datoCmsAsset?.gatsbyImageData} alt={title!} />
        <div className="container-text">
          <div className="container">
            <div className="row">
              <div className="col">
                <h2>{title}</h2>
              </div>
            </div>
            {filter && (
              <div className="row link-read-more white">
                <div className="col">
                  <Link to={generateUrl(filter?.__typename, filter.slug)}>
                    {modelTopicMessages.button}
                  </Link>
                </div>
              </div>
            )}
            <div className="row main-item">
              <div className="col-lg-7 mr-lg-0 pr-lg-0">
                <div className="image-container">
                  <Link to={url}>
                    <Image
                      image={
                        featuredImage?.gatsbyImageData ??
                        imgFeaturedMultipleContentFeatured?.desktop
                      }
                      alt={title!}
                    />
                  </Link>
                  {/* Show upcoming event label for events marked as pre-events */}
                  {isUpcomingEvent(featureContent) && <ImageLabel text="Upcoming Event" />}
                </div>
              </div>
              <div className="col-lg-5 ml-0 pl-0 col-text">
                <div>
                  <TitleLink
                    htmltag={'h3'}
                    to={url}
                    title={externalName!}
                    model={modelMessages.label}
                    isWhite={true}
                  />

                  <p className="description">{excerpt}</p>
                  <Button to={url} title={modelMessages.button} isBgDark />
                </div>
              </div>
            </div>
            <div className="row mt-lg-5 row-more-items">
              {nodes
                .filter((item) => item?.originalId !== originalId)
                .slice(0, 3)
                .map((item) => {
                  if (!item) {
                    return null
                  }
                  const modelMessages = getModelMessages(item.__typename)
                  const url = generateUrl(item.__typename, item.slug)
                  return (
                    <div
                      className="col-lg-4"
                      key={`highlightedv2-${item.slug}`}
                    >
                      <div>
                        <TitleLink
                          htmltag={'h3'}
                          to={url}
                          title={item.externalName!}
                          model={modelMessages.label}
                          isWhite={true}
                        />
                        <p>{item.excerpt}</p>
                      </div>
                    </div>
                  )
                })}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default FeaturedContentsHighlightFeatured
